import { APP_ENV, fetchGet, fetchPost } from "..";
import { APIResponse } from "../../types/responses/APIResponse";
import { AllPreferences, User, StreamSessions } from "../../types/user";
import { STORAGE_KEYS, storage } from "../../utils/storage";

export const updateMemberPreferences = async (user: User) => {
  const formattedData = {
    preference: {
      member: { ...user.member },
    },
  };
  return await fetchPost("member/preferences", {}, formattedData, "PUT");
};

export async function fetchOrInitializeAnonUserFromStorage(): Promise<
  User | AllPreferences
> {
  try {
    const anonData = await storage.load({
      key: STORAGE_KEYS.ANON_USER_DETAILS,
    });

    if (anonData?.member) {
      if (APP_ENV == "integration") {
        console.log("******** RETURNING ADJUSTED INTEGRATION USER ********");
        const integrationUser = {
          member: {
            ...anonData.member,
            timeform_display: true,
            oddschecker_display: true,
            form_display: true,
            tracker_display: true,
            details_display: true,
            riq_display: true,
          },
        };

        console.log("Integration User:", integrationUser);
        return integrationUser;
      }

      // already have anon user with settings → use it
      return anonData as User;
    }
  } catch (e) {
    console.log("[userUpdateAtom] No anon data in storage yet:", e);
  }

  // nothing in storage → initialise with DEFAULT_USER
  await storage.save({
    key: STORAGE_KEYS.ANON_USER_DETAILS,
    data: DEFAULT_USER,
  });

  if (APP_ENV == "integration") {
    console.log("******** RETURNING ADJUSTED INTEGRATION USER ********");
    return {
      member: {
        ...DEFAULT_USER.member,
        timeform_display: true,
        oddschecker_display: true,
        form_display: true,
        tracker_display: true,
        details_display: true,
        riq_display: true,
      },
    };
  } else {
    console.log("******** RETURNING DEFAULT USER ********");
    return DEFAULT_USER;
  }
}

export const updateLocalStorageUser = async ({
  updatedUser,
  anon = false,
}: {
  updatedUser: User;
  anon?: boolean;
}) => {
  // Save updated user data
  await storage.save({
    key: anon ? STORAGE_KEYS.ANON_USER_DETAILS : STORAGE_KEYS.USER_DETAILS,
    data: updatedUser,
  });
};

const DEFAULT_USER: AllPreferences = {
  member: {
    odds_display: "fractions",
    racecard_display: "price",
    timeform_display: false,
    oddschecker_display: false,
    form_display: false,
    tracker_display: false,
    details_display: false,
    default_sort: "cloth_number",
    notification_methods: [],
    notification_types: [],
    marketing_email: false,
    marketing_phone: false,
    marketing_sms: false,
    marketing_post: false,
    marketing_gdpr_notified: true,
    bookmaker_display: {
      best_odds: true,
    },
    consents: {
      consent_bet365: false,
      consent_betbright: false,
      consent_betfair: false,
      consent_betvictor: false,
      consent_betway: false,
      consent_coral: false,
      consent_ladbrokes: false,
      consent_paddypower: false,
      consent_skybet: false,
      consent_williamhill: false,
      consent_racecourse_affiliates: false,
      consent_ruk: false,
      consent_bookmakers: false,
      consent_age: true,
      consent_other: false,
    },
  },
} as AllPreferences;

export const getStreamSessions = async (): Promise<
  APIResponse<StreamSessions>
> => {
  return await fetchGet("member/stream_sessions");
};

// Delete current stream session if any
export const deleteCurrentStreamSession = async (): Promise<
  APIResponse<null>
> => {
  return await fetchPost("member/stream_session", {}, {}, "DELETE");
};

export const deleteStreamSession = async (
  sessionToken: string
): Promise<APIResponse<null>> => {
  return await fetchPost(
    `member/stream_sessions/${sessionToken}`,
    {},
    {},
    "DELETE"
  );
};

export const deleteAllStreamSessions = async (): Promise<APIResponse<null>> => {
  return await fetchPost(`member/stream_sessions/all`, {}, {}, "DELETE");
};
